
export const PST_FALLBACK_ZONE = 'America/Los_Angeles';

export type Instant = Date | string;

export function toInstant(value: Instant): Date {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new TypeError(`Not a valid instant: ${String(value)}`);
  }
  return date;
}

function formatterForZone(zone: string | null | undefined): Intl.DateTimeFormat {
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  };

  if (zone && zone.trim() !== '') {
    try {
      return new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone });
    } catch {
    }
  }
  return new Intl.DateTimeFormat('en-US', { ...options, timeZone: PST_FALLBACK_ZONE });
}

export function formatInZone(instant: Instant, zone: string | null | undefined): string {
  const date = toInstant(instant);
  return formatterForZone(zone).format(date);
}

function clockFormatterForZone(zone: string | null | undefined): Intl.DateTimeFormat {
  const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
  if (zone && zone.trim() !== '') {
    try {
      return new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone });
    } catch {
    }
  }
  return new Intl.DateTimeFormat('en-US', { ...options, timeZone: PST_FALLBACK_ZONE });
}

function clockPieces(instant: Instant, zone: string | null | undefined): { hm: string; period: string } {
  const parts = clockFormatterForZone(zone).formatToParts(toInstant(instant));
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
  const period = parts.find((p) => p.type === 'dayPeriod')?.value ?? '';
  return { hm: `${hour}:${minute}`, period };
}

// No timezone label — the zone is shown once, in the calendar's corner. → docs/time-and-timezones.md
export function formatClock(instant: Instant, zone: string | null | undefined): string {
  const { hm, period } = clockPieces(instant, zone);
  return period ? `${hm} ${period}` : hm;
}

// "1:00 – 2:00 PM": meridiem printed once, or on both ends when the range crosses noon/midnight.
export function formatClockRange(
  start: Instant,
  end: Instant,
  zone: string | null | undefined,
): string {
  const a = clockPieces(start, zone);
  const b = clockPieces(end, zone);
  if (a.period && a.period === b.period) {
    return `${a.hm} – ${b.hm} ${b.period}`;
  }
  return `${formatClock(start, zone)} – ${formatClock(end, zone)}`;
}
